import React, {
    useState,
    useMemo,
    useRef,
    useEffect,
    useLayoutEffect
} from 'react'

// Custom start/end date range calendar popup.
// Adapted from the buyer's TypeScript implementation to this project (JS + brand colors).
const CustomDateRangePopup = ({
    isOpen,
    onClose,
    onApply,
    initialFromDate,
    initialToDate,
    triggerRef
}) => {
    const popupRef = useRef(null)
    const [selectedStartDate, setSelectedStartDate] = useState(
        initialFromDate ? new Date(initialFromDate) : null
    )
    const [selectedEndDate, setSelectedEndDate] = useState(
        initialToDate ? new Date(initialToDate) : null
    )
    const [currentMonth, setCurrentMonth] = useState(
        initialFromDate
            ? new Date(initialFromDate)
            : initialToDate
            ? new Date(initialToDate)
            : new Date()
    )
    const [calculatedPosition, setCalculatedPosition] = useState('left')
    const [isVisible, setIsVisible] = useState(false)

    useEffect(() => {
        setSelectedStartDate(initialFromDate ? new Date(initialFromDate) : null)
        setSelectedEndDate(initialToDate ? new Date(initialToDate) : null)
    }, [initialFromDate, initialToDate])

    // Position the popup (top + left/right) synchronously before the browser
    // paints, so it never flashes at the wrong side and causes a horizontal
    // scrollbar. Fade it in afterwards for a smooth appearance.
    useLayoutEffect(() => {
        if (!isOpen) {
            setIsVisible(false)
            return undefined
        }

        const calculatePosition = () => {
            if (!triggerRef?.current || !popupRef?.current) return
            const trigger = triggerRef.current
            const popup = popupRef.current

            popup.style.top = `${
                trigger.offsetTop + trigger.offsetHeight + 8
            }px`

            const triggerRect = trigger.getBoundingClientRect()
            const viewportWidth = window.innerWidth
            const popupWidth = popup.getBoundingClientRect().width || 400

            const spaceOnLeft = triggerRect.left
            const spaceOnRight = viewportWidth - triggerRect.right

            if (spaceOnRight < popupWidth && spaceOnLeft >= popupWidth) {
                setCalculatedPosition('right')
            } else {
                setCalculatedPosition('left')
            }
        }

        calculatePosition()
        setIsVisible(true)

        window.addEventListener('resize', calculatePosition)
        return () => {
            window.removeEventListener('resize', calculatePosition)
        }
    }, [isOpen, triggerRef])

    useEffect(() => {
        if (!isOpen) return undefined

        const handleClickOutside = (event) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(event.target) &&
                triggerRef?.current &&
                !triggerRef.current.contains(event.target)
            ) {
                onClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen, onClose, triggerRef])

    const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
    const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
    ]

    const getDaysInMonth = (date) => {
        const year = date.getFullYear()
        const month = date.getMonth()
        const firstDay = new Date(year, month, 1)
        const lastDay = new Date(year, month + 1, 0)
        const daysInMonth = lastDay.getDate()
        const startingDayOfWeek = firstDay.getDay()

        const days = []
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null)
        }
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(i)
        }
        return days
    }

    const getPreviousMonthDays = (date) => {
        const year = date.getFullYear()
        const month = date.getMonth()
        const firstDay = new Date(year, month, 1)
        const startingDayOfWeek = firstDay.getDay()

        const prevMonthLastDay = new Date(year, month, 0)
        const daysInPrevMonth = prevMonthLastDay.getDate()
        const days = []

        const startDay = daysInPrevMonth - startingDayOfWeek + 1
        for (let i = Math.max(1, startDay); i <= daysInPrevMonth; i++) {
            days.push(i)
        }
        return days
    }

    const getNextMonthDays = (date) => {
        const totalCells = 42
        const daysInMonth = new Date(
            date.getFullYear(),
            date.getMonth() + 1,
            0
        ).getDate()
        const firstDay = new Date(
            date.getFullYear(),
            date.getMonth(),
            1
        ).getDay()
        const totalDaysShown = firstDay + daysInMonth
        const remainingCells = totalCells - totalDaysShown
        const days = []
        for (let i = 1; i <= remainingCells; i++) {
            days.push(i)
        }
        return days
    }

    const handleDateClick = (day, isPrevMonth, isNextMonth) => {
        if (isPrevMonth || isNextMonth) return

        const clickedDate = new Date(
            currentMonth.getFullYear(),
            currentMonth.getMonth(),
            day
        )

        if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
            setSelectedStartDate(clickedDate)
            setSelectedEndDate(null)
        } else if (selectedStartDate && !selectedEndDate) {
            if (clickedDate < selectedStartDate) {
                setSelectedEndDate(selectedStartDate)
                setSelectedStartDate(clickedDate)
            } else {
                setSelectedEndDate(clickedDate)
            }
        }
    }

    const createDateForDay = (day, monthOffset) => {
        try {
            const year = currentMonth.getFullYear()
            const month = currentMonth.getMonth() + monthOffset
            const date = new Date(year, month, day)
            if (
                date.getFullYear() !== year ||
                date.getMonth() !== month ||
                date.getDate() !== day
            ) {
                return null
            }
            return date
        } catch {
            return null
        }
    }

    const normalizeDate = (d) =>
        new Date(d.getFullYear(), d.getMonth(), d.getDate())

    const isDateInRange = (day, isPrevMonth, isNextMonth, monthOffset = 0) => {
        if (!selectedStartDate || !selectedEndDate) return false

        const date = createDateForDay(day, monthOffset)
        if (!date) return false

        const normalizedDate = normalizeDate(date)
        const normalizedStart = normalizeDate(selectedStartDate)
        const normalizedEnd = normalizeDate(selectedEndDate)
        return (
            normalizedDate >= normalizedStart && normalizedDate <= normalizedEnd
        )
    }

    const isDateSelected = (day, isPrevMonth, isNextMonth, monthOffset = 0) => {
        if (!selectedStartDate) return false

        const date = createDateForDay(day, monthOffset)
        if (!date) return false

        const normalizedDate = normalizeDate(date)
        const normalizedStart = normalizeDate(selectedStartDate)
        if (normalizedDate.getTime() === normalizedStart.getTime()) return true
        if (selectedEndDate) {
            const normalizedEnd = normalizeDate(selectedEndDate)
            if (normalizedDate.getTime() === normalizedEnd.getTime())
                return true
        }
        return false
    }

    const handlePrevMonth = () => {
        setCurrentMonth(
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
        )
    }

    const handleNextMonth = () => {
        setCurrentMonth(
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
        )
    }

    const handleApply = () => {
        if (selectedStartDate && selectedEndDate) {
            const formatDate = (date) => {
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')
                return `${year}-${month}-${day}`
            }
            onApply(formatDate(selectedStartDate), formatDate(selectedEndDate))
            onClose()
        }
    }

    const days = useMemo(() => getDaysInMonth(currentMonth), [currentMonth])
    const prevMonthDays = useMemo(
        () => getPreviousMonthDays(currentMonth),
        [currentMonth]
    )
    const nextMonthDays = useMemo(
        () => getNextMonthDays(currentMonth),
        [currentMonth]
    )

    if (!isOpen) return null

    const dayClass = (isSelected, inRange, isMuted) => {
        if (isSelected) return 'bg-[#266ef1] text-white'
        if (inRange) return 'bg-[#dee9fe] text-[#21242b]'
        if (isMuted) return 'text-[#babbbd] cursor-default'
        return 'text-[#4d5055] hover:bg-[#f8f8f8]'
    }

    return (
        <div
            ref={popupRef}
            className={`absolute z-50 bg-white shadow-lg border border-[#e9e9ea] w-[270px] sm:w-[400px] origin-top transition-all duration-150 ease-out ${
                calculatedPosition === 'right' ? 'right-0' : 'left-0'
            } ${
                isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 -translate-y-1 pointer-events-none'
            }`}
        >
            <div className='p-5'>
                <div className='flex items-center justify-between mb-4'>
                    <h3 className='regular-16 weight-700 text-[#21242b]'>
                        {monthNames[currentMonth.getMonth()]}{' '}
                        {currentMonth.getFullYear()}
                    </h3>
                    <div className='flex gap-2'>
                        <button
                            type='button'
                            onClick={handlePrevMonth}
                            className='w-8 h-8 flex items-center justify-center bg-[#f8f8f8] hover:bg-[#e9e9ea] transition-colors text-[#6a6c71]'
                        >
                            &lt;
                        </button>
                        <button
                            type='button'
                            onClick={handleNextMonth}
                            className='w-8 h-8 flex items-center justify-center bg-[#f8f8f8] hover:bg-[#e9e9ea] transition-colors text-[#6a6c71]'
                        >
                            &gt;
                        </button>
                    </div>
                </div>

                <div className='mb-5'>
                    <div className='grid grid-cols-7 gap-1 mb-2'>
                        {daysOfWeek.map((day, index) => (
                            <div
                                key={`day-${index}`}
                                className='text-center regular-12 weight-500 text-[#6a6c71] py-2'
                            >
                                {day}
                            </div>
                        ))}
                    </div>

                    <div className='grid grid-cols-7 gap-1'>
                        {prevMonthDays.map((day) => {
                            const inRange = isDateInRange(day, true, false, -1)
                            const isSelected = isDateSelected(
                                day,
                                true,
                                false,
                                -1
                            )
                            return (
                                <button
                                    type='button'
                                    key={`prev-${day}`}
                                    className={`text-center regular-14 py-2 ${dayClass(
                                        isSelected,
                                        inRange,
                                        true
                                    )}`}
                                    disabled={!isSelected && !inRange}
                                >
                                    {day}
                                </button>
                            )
                        })}

                        {days.map((day) => {
                            if (day === null) return null
                            const inRange = isDateInRange(day, false, false)
                            const isSelected = isDateSelected(day, false, false)
                            return (
                                <button
                                    type='button'
                                    key={`current-${day}`}
                                    onClick={() =>
                                        handleDateClick(day, false, false)
                                    }
                                    className={`text-center regular-14 py-2 transition-colors ${dayClass(
                                        isSelected,
                                        inRange,
                                        false
                                    )}`}
                                >
                                    {day}
                                </button>
                            )
                        })}

                        {nextMonthDays.map((day) => {
                            const inRange = isDateInRange(day, false, true, 1)
                            const isSelected = isDateSelected(
                                day,
                                false,
                                true,
                                1
                            )
                            return (
                                <button
                                    type='button'
                                    key={`next-${day}`}
                                    className={`text-center regular-14 py-2 ${dayClass(
                                        isSelected,
                                        inRange,
                                        true
                                    )}`}
                                    disabled={!isSelected && !inRange}
                                >
                                    {day}
                                </button>
                            )
                        })}
                    </div>
                </div>

                <button
                    type='button'
                    onClick={handleApply}
                    disabled={!selectedStartDate || !selectedEndDate}
                    className='w-full py-3 bg-[#266ef1] text-white weight-500 regular-14 hover:bg-[#1948a3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                >
                    Apply Now
                </button>
            </div>
        </div>
    )
}

export default CustomDateRangePopup
